/**
 * ClauseCard — the standard wrapper used by every clause sub-renderer in the
 * basic SELECT renderer. Provides a consistent visual hierarchy (header label
 * + optional accessory + body) and a stable `data-testid` hook for E2E specs.
 *
 * `density='compact'` (prim #1 §4) collapses the card to a single-line row —
 * `[TITLE] [body] [accessory] [footer]` — the keyword row used for EMPTY
 * optional clauses on both surfaces (e.g. `GROUP BY — none · [+ Group by
 * column]`, `WHERE — none · [+ Add]`) and for the one-line LIMIT row, so a
 * clause with nothing in it no longer eats a full card.
 *
 * `density='line'` is the populated FROM / SELECT / WHERE / HAVING / GROUP BY /
 * ORDER BY row:
 * the title sits in a left column and the body wraps beside it, with the
 * footer (the add action) following the last chip and the accessory pushed to
 * the right end of that same wrapping row — so a clause whose content fits on
 * one line is one line, and in a narrow popover the accessory wraps onto its
 * own line instead of squeezing (or overlapping) the chips. Titles
 * of line and compact rows share one minimum width, so their chips line up.
 * The default card (title above the body, footer below) remains for the
 * stacked Rail view of WHERE / HAVING.
 */

import type { ReactNode } from 'react';

/** Body text of an empty keyword row — shared by every empty-clause row. */
export const EMPTY_CLAUSE_LABEL = '— none';

/** Title column of line and compact rows: one minimum width so chips align. */
export const CLAUSE_TITLE_COLUMN = 'min-w-[4.5rem] shrink-0';

export interface ClauseCardProps {
  readonly title: string;
  readonly testId: string;
  readonly accessory?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  /**
   * `'compact'`: a single-line keyword row (empty clauses, LIMIT). `'line'`: a
   * populated clause with its title to the left of its wrapping content.
   */
  readonly density?: 'default' | 'compact' | 'line';
  /** Compact rows: `'destructive'` tints the row red (an UPDATE / DELETE with no WHERE). */
  readonly tone?: 'default' | 'destructive';
}

export function ClauseCard({
  title,
  testId,
  accessory,
  children,
  footer,
  density = 'default',
  tone = 'default',
}: ClauseCardProps) {
  if (density === 'line') {
    return (
      <section
        className="flex items-start gap-2 rounded-md border border-border bg-card px-3 py-1.5"
        data-testid={testId}
        data-density="line"
      >
        <span
          className={`${CLAUSE_TITLE_COLUMN} pt-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground`}
          data-testid={`${testId}-title`}
        >
          {title}
        </span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1.5 text-sm">
          <div className="min-w-0 max-w-full">{children}</div>
          {footer ? <div className="shrink-0">{footer}</div> : null}
          {accessory ? <div className="ml-auto shrink-0">{accessory}</div> : null}
        </div>
      </section>
    );
  }
  if (density === 'compact') {
    const destructive = tone === 'destructive';
    return (
      <section
        className={`flex flex-wrap items-center gap-2 rounded-md border px-3 py-1 ${destructive ? 'border-destructive/60 bg-destructive/5' : 'border-border bg-card'}`}
        data-testid={testId}
        data-density="compact"
        data-tone={destructive ? tone : undefined}
      >
        <span
          className={`${CLAUSE_TITLE_COLUMN} text-xs font-semibold uppercase tracking-wide ${destructive ? 'text-destructive dark:text-red-400' : 'text-muted-foreground'}`}
          data-testid={`${testId}-title`}
        >
          {title}
        </span>
        <div className="min-w-0 flex-1 text-sm">{children}</div>
        {accessory}
        {footer ? <div className="shrink-0">{footer}</div> : null}
      </section>
    );
  }
  return (
    <section
      className="rounded-lg border border-border bg-card px-3 py-2 shadow-sm"
      data-testid={testId}
    >
      <header className="flex items-baseline justify-between gap-2 pb-1">
        <span
          className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          data-testid={`${testId}-title`}
        >
          {title}
        </span>
        {accessory}
      </header>
      <div className="text-sm">{children}</div>
      {footer ? <div className="mt-1.5">{footer}</div> : null}
    </section>
  );
}
